import {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  analyse,
  compile,
  createPlan,
  parse,
  projectionV2,
  structureReport,
  withFindingIds,
} from '../../../../services/EpisodeScriptService';
import type {
  EditRequest,
  Fixture,
  IdentifiedFinding,
  NamesTable,
  Plan,
  SidecarMap3,
  StructuralItem,
} from '../../../../dorian-shared/types/episodeScript';
import {
  changeItems,
  changePhrase,
  engineWords,
  graphOfFixture,
  sceneHeadings,
} from '../viewModel/compactGraph';
import { changeWords } from '../viewModel/changeRows';
import { publicNames } from '../viewModel/publicNames';
import { rowRefusal } from '../viewModel/refusals';
import type {
  BasilAnalysis,
  BasilPlanRow,
  BasilRequestStatus,
  BasilSendability,
} from '../types/basilTypes';
import { BASIL_DEBOUNCE_MS } from '../types/basilTypes';

const statusOfStructural = (item: StructuralItem): BasilRequestStatus => {
  if (item.status === 'needs the owner') {
    return 'needs-owner';
  }

  return 'needs-canary';
};

const planRowsOf = (
  requests: EditRequest[],
  structural: StructuralItem[],
  plans: Array<Plan | null>,
  refusals: Array<string | null>,
  text: string,
  map: SidecarMap3 | null,
  fixture: Fixture,
  /** the READ fixture: where the `before` of every row comes from. */
  before: Fixture,
  namesTable: NamesTable | null,
): BasilPlanRow[] => {
  const graph = graphOfFixture(fixture);
  const headings = sceneHeadings(map, text);
  const names = publicNames(namesTable);
  const rows: BasilPlanRow[] = requests.map((request, index) => {
    const plan = plans[index] ?? null;
    const refusal = refusals[index] ?? null;
    let status: BasilRequestStatus = 'planned';
    if (refusal) {
      status = 'refused';
    } else if (plan && plan.needsCanary) {
      status = 'needs-canary';
    }

    return {
      key: plan?.planId ?? `${engineWords(request)}#${index}`,
      phrase: changePhrase(request, headings, graph),
      // P4: the ROW shows the sentence, before and after. The `before` is read off the fixture the
      // script was decompiled from, never off the compiled one, which already carries the edit.
      words: changeWords(request, before, names, headings, graph),
      engineWords: engineWords(request),
      status,
      message: refusal ?? undefined,
      chunkIndex: 0,
      planId: plan?.planId ?? null,
      branchId: request.branchId == null ? null : String(request.branchId),
      expectedCounts: plan?.expectedCounts ?? null,
      countsMode: plan?.countsMode ?? null,
    };
  });
  structural.forEach((item, index) => {
    // P5: the held row says the limitation and an action that exists. Where this view model has no
    // sentence of its own the compiler's own reason stands, verbatim, exactly as it did.
    const refusal = rowRefusal(item.kind, item.reason);

    rows.push({
      key: `structural#${index}#${item.kind}`,
      phrase: changePhrase(item, headings, graph),
      engineWords: engineWords(item),
      status: statusOfStructural(item),
      message: refusal ? refusal.text : item.reason,
      opensScene: refusal ? refusal.opensScene : false,
      chunkIndex: 0,
      planId: null,
      branchId: item.branchId == null ? null : String(item.branchId),
      expectedCounts: null,
      countsMode: null,
    });
  });

  return rows;
};

/**
 * A row may be sent when the planner accepted it and nothing gates it.
 *
 * `planned` with a plan id is the only sendable state: `refused`, `needs-canary` and `needs-owner`
 * all block, and so does a row whose plan never materialised.
 */
function isSendableRow(row: BasilPlanRow): boolean {
  return row.status === 'planned' && row.planId !== null;
}

/** The Apply decision, over every change row — never over the plans that happened to survive. */
export function sendabilityOf(rows: readonly BasilPlanRow[]): BasilSendability {
  let sendable = 0;
  let blocked = 0;
  let firstBlockingMessage: string | null = null;

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];

    if (isSendableRow(row)) {
      sendable += 1;
    } else {
      blocked += 1;

      if (firstBlockingMessage === null) {
        firstBlockingMessage = row.message || "Basil can't send this one yet.";
      }
    }
  }

  return {
    total: rows.length, sendable, blocked, firstBlockingMessage,
  };
}

/**
 * The debounced half of the pipeline, run once (spec §4.5).
 *
 * `parse` → `compile` → `structureReport` → `analyse` → `withFindingIds` → `projectionV2` →
 * `createPlan` per request. A mapped compile carries no `report` key, so the report is computed
 * here; a plan the planner refuses keeps its refusal message verbatim and becomes a `refused` row
 * rather than disappearing.
 *
 * **Every request is planned against the ORIGINAL read fixture** — the immutable one the read lane
 * returned, the bytes the script was decompiled from. The compiled fixture already carries the
 * writer's edit, so planning against it showed the planner the requested text already in place and
 * it refused every text edit as `request is a no-op` (`plan.ts:447`, `:465`). The compiled fixture
 * is the projection's and the preview's, and nothing else's.
 */
export function runAnalysis(
  text: string,
  map: SidecarMap3 | null,
  fixture: Fixture,
  names: NamesTable | null,
): BasilAnalysis {
  const ast = parse(text, names ? { names } : {});
  const compiled = map
    ? compile(ast, { sidecar: map, fixture })
    : compile(ast, {});
  const report = compiled.mode === 'mapped'
    ? structureReport(compiled.nodes, compiled.markers)
    : compiled.report;
  const identified = withFindingIds(analyse({ ...compiled, report }, text));
  const findings: IdentifiedFinding[] = identified.findings ?? [];
  const requests: EditRequest[] = compiled.mode === 'mapped' ? compiled.requests : [];
  const structural: StructuralItem[] = compiled.mode === 'mapped' ? compiled.structural : [];
  const nextFixture = compiled.mode === 'mapped' ? compiled.fixture : fixture;
  const plans: Array<Plan | null> = [];
  const refusals: Array<string | null> = [];
  requests.forEach((request) => {
    try {
      // The ORIGINAL read, never `nextFixture`: the plan describes the move FROM what the server
      // holds TO what the writer typed, and a fixture that already carries the edit has no move in
      // it to describe.
      plans.push(createPlan(fixture, request));
      refusals.push(null);
    } catch (error) {
      plans.push(null);
      refusals.push(error instanceof Error ? error.message : String(error));
    }
  });
  const markedLines = new Set<number>();
  (compiled.markers ?? []).forEach((marker) => markedLines.add(Number(marker.line)));
  findings
    .filter((finding) => finding.rule === 'R7' && finding.line != null)
    .forEach((finding) => markedLines.add(Number(finding.line)));

  const rows = planRowsOf(requests, structural, plans, refusals, text, map, nextFixture, fixture, names);

  return {
    analysedText: text,
    compiled: {
      mode: compiled.mode,
      fixture: nextFixture,
      nodes: compiled.nodes,
    },
    report,
    findings,
    projection: compiled.mode === 'mapped' ? projectionV2(compiled.fixture) : null,
    // The preview's `before`, taken with the same function over the read the script opened with, so
    // a scene whose projection line moved is marked as changed (spec §4.10).
    beforeProjection: projectionV2(fixture),
    plans: plans.filter((plan): plan is Plan => plan != null),
    rows,
    requests,
    structural,
    markedLines,
    changeCount: changeItems(requests, structural).length,
    sendability: sendabilityOf(rows),
  };
}

/**
 * 400 ms after the last keystroke, and only this half is debounced. A newer edit abandons the
 * in-flight run: the run id is monotonic and a result whose id is not the latest is dropped.
 */
export function useDebouncedAnalysis(
  text: string,
  map: SidecarMap3 | null,
  fixture: Fixture | null,
  names: NamesTable | null,
  delayMs: number = BASIL_DEBOUNCE_MS,
): { analysis: BasilAnalysis | null; isAnalysing: boolean; errorMessage: string | null } {
  const [analysis, setAnalysis] = useState<BasilAnalysis | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const runIdRef = useRef(0);

  useEffect(() => {
    if (!fixture) {
      setAnalysis(null);

      return undefined;
    }
    runIdRef.current += 1;
    const runId = runIdRef.current;
    setIsAnalysing(true);
    const timer = setTimeout(() => {
      let next: BasilAnalysis | null = null;
      let failure: string | null = null;
      try {
        next = runAnalysis(text, map, fixture, names);
      } catch (error) {
        failure = error instanceof Error ? error.message : String(error);
      }
      if (runIdRef.current !== runId) {
        return;
      }
      setIsAnalysing(false);
      setErrorMessage(failure);
      if (next) {
        setAnalysis(next);
      }
    }, delayMs);

    return () => clearTimeout(timer);
  }, [text, map, fixture, names, delayMs]);

  return { analysis, isAnalysing, errorMessage };
}
